import React, { useState } from 'react';
import axios from 'axios';
import styled from 'styled-components';
import { FocusableSection, FocusableButton } from '../components/Focusable';
import { API_URL } from '../config';

const Container = styled.div`
  padding: 2rem;
  max-width: 450px;
  margin: auto;
  display: flex;
  flex-direction: column;
  align-items: center;
  min-height: 100vh;
  justify-content: center;

  @media (max-width: 768px) {
    padding: 1rem;
    justify-content: flex-start;
    padding-top: 3rem;
  }
`;

const Card = styled.div`
  background: linear-gradient(145deg, #1a1d24, #15171c);
  border-radius: 16px;
  padding: 2.5rem;
  width: 100%;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.4);
  border: 1px solid rgba(99, 44, 228, 0.2);

  @media (max-width: 768px) {
    padding: 1.5rem;
    border-radius: 12px;
  }
`;

const Title = styled.h1`
  color: #fff;
  text-align: center;
  margin-bottom: 2rem;
  font-size: 2rem;
  font-weight: 600;

  @media (max-width: 768px) {
    font-size: 1.5rem;
    margin-bottom: 1.5rem;
  }
`;

const Form = styled.form`
  width: 100%;
`;

const Input = styled.input`
  width: 100%;
  padding: 0.875rem;
  margin: 0.75rem 0;
  border: 1px solid rgba(255, 255, 255, 0.1);
  border-radius: 8px;
  background: rgba(0, 0, 0, 0.3);
  color: #fff;
  font-size: 1rem;
  outline: none;
  transition: all 0.3s ease;

  &::placeholder {
    color: #666;
  }

  &:focus {
    border-color: #632ce4;
    background: rgba(99, 44, 228, 0.1);
  }
`;

const Button = styled.button`
  width: 100%;
  color: #fff;
  text-decoration: none;
  padding: 1rem;
  border: none;
  border-radius: 8px;
  background: linear-gradient(135deg, #632ce4, #9d50bb);
  cursor: pointer;
  transition: all 0.3s ease;
  margin-top: 1rem;
  font-size: 1.1rem;
  font-weight: 600;
  box-shadow: 0 4px 15px rgba(99, 44, 228, 0.3);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 6px 20px rgba(99, 44, 228, 0.5);
  }

  &:active {
    transform: translateY(0);
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
    transform: none;
  }
`;

const Error = styled.div`
  background: rgba(231, 76, 60, 0.1);
  border: 1px solid rgba(231, 76, 60, 0.3);
  color: #e74c3c;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const Success = styled.div`
  background: rgba(39, 174, 96, 0.1);
  border: 1px solid rgba(39, 174, 96, 0.3);
  color: #27ae60;
  padding: 0.75rem;
  border-radius: 8px;
  font-size: 0.9rem;
  margin-bottom: 1rem;
  text-align: center;
`;

const PasswordHint = styled.p`
  color: #888;
  font-size: 0.8rem;
  margin: 0.25rem 0 0.75rem 0;
  font-style: italic;
`;

const LinkButton = styled.button`
  background: transparent;
  border: none;
  color: #9d50bb;
  cursor: pointer;
  font-size: 0.95rem;
  margin-top: 1.5rem;
  width: 100%;
  text-align: center;
  padding: 0.5rem;
  transition: color 0.3s ease;

  &:hover {
    color: #632ce4;
  }
`;

const ResetPassword = ({ resetToken, setCurrentPage }) => {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    if (newPassword.length < 8) {
      setError('Le mot de passe doit contenir au moins 8 caracteres');
      return;
    }

    if (newPassword !== confirmPassword) {
      setError('Les mots de passe ne correspondent pas');
      return;
    }

    setLoading(true);

    try {
      await axios.post(`${API_URL}/auth/password-reset`, {
        reset_token: resetToken,
        new_password: newPassword
      });
      setSuccess('Mot de passe mis a jour ! Vous pouvez maintenant vous connecter.');
      setNewPassword('');
      setConfirmPassword('');

      setTimeout(() => {
        setCurrentPage('login');
      }, 3000);
    } catch (err) {
      setError(err.response?.data?.error || 'Erreur lors de la reinitialisation');
    } finally {
      setLoading(false);
    }
  };

  return (
    <FocusableSection focusKey="RESET_PASSWORD_PAGE">
      <Container>
        <Card>
          <Title>Nouveau mot de passe</Title>

          {error && <Error>{error}</Error>}
          {success && <Success>{success}</Success>}

          {!success && (
            <Form onSubmit={handleSubmit}>
              <Input
                type="password"
                placeholder="Nouveau mot de passe"
                value={newPassword}
                onChange={(e) => setNewPassword(e.target.value)}
                required
                disabled={loading}
                autoFocus
              />
              <PasswordHint>
                Le mot de passe doit contenir au moins 8 caracteres
              </PasswordHint>
              <Input
                type="password"
                placeholder="Confirmer le mot de passe"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                required
                disabled={loading}
              />
              <FocusableButton
                as={Button}
                type="submit"
                disabled={loading}
                onPress={() => {}}
              >
                {loading ? 'Reinitialisation...' : 'Reinitialiser le mot de passe'}
              </FocusableButton>
            </Form>
          )}

          <LinkButton onClick={() => setCurrentPage('login')}>
            Retour a la connexion
          </LinkButton>
        </Card>
      </Container>
    </FocusableSection>
  );
};

export default ResetPassword;
